import { useI18nContext } from '@lemonade-hq/nuiza';
import type { Percentage, Seconds } from '@lemonade-hq/ts-helpers';
import type { LevelOneProgressItem, UniclientAction } from '@lemonade-hq/uniclient-chat';
import { Drawer, ellipsis, Flex, getPixelValue, spacing, Text } from '@lemonade-hq/uniclient-theme';
import type { Transition } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import { forwardRef, useMemo, useState } from 'react';
import { WIZARD_PROGRESS_ELEMENT_ID } from '../utils/consts';
import * as styles from './WizardMobileProgress.css';
import { WizardProgress } from './WizardProgress';
import { parseItemId } from './WizardProgress/utils';

type ProgressItemWithStepsCount = LevelOneProgressItem & {
  readonly stepsCount: number;
};

interface WizardMobileProgressProps {
  readonly currentStepIndex: number;
  readonly currentItemId: string;
  readonly items: ProgressItemWithStepsCount[];
  readonly visibleDots?: number;
  readonly onAction?: (action: UniclientAction) => Promise<unknown>;
}

const STEP_COMPLETED_OPACITY = 0.5;
const STEP_NOT_COMPLETED_OPACITY = 1;
const CURRENT_DOT_DELAY: Seconds = 0.3;
const MIN_DOT_FILL_AMOUNT: Percentage = 0.33;
const DEFAULT_VISIBLE_DOTS = 4;
const PROGRESS_ANIMATION_DURATION: Seconds = 0.1;

const springAnimationConfiguration: Transition = {
  type: 'spring',
  damping: 15,
  stiffness: 120,
  mass: 1,
  duration: 0.5,
};

const dotVariants = {
  open: {
    width: styles.DOT_OPENED_WIDTH,
    height: styles.DOT_CLOSED_SIZE,
  },
  closed: {
    width: styles.DOT_CLOSED_SIZE,
    height: styles.DOT_CLOSED_SIZE,
  },
};

interface DotProps {
  readonly current: boolean;
  readonly fill?: number;
  readonly stepCompleted?: boolean;
}

const Dot = forwardRef<HTMLDivElement, DotProps>(({ current, fill = 0, stepCompleted = false }, ref) => {
  return (
    <motion.div
      animate={current ? 'open' : 'closed'}
      className={styles.dot}
      initial={dotVariants.open}
      layout
      ref={ref}
      transition={springAnimationConfiguration}
      variants={dotVariants}
    >
      <motion.div
        animate={{
          width: `${(stepCompleted ? 1 : fill) * 100}%`,
          minWidth: current ? styles.DOT_CLOSED_SIZE : 0,
          opacity: stepCompleted ? STEP_COMPLETED_OPACITY : STEP_NOT_COMPLETED_OPACITY,
          transition: {
            delay: current ? CURRENT_DOT_DELAY : 0,
            opacity: {
              delay: CURRENT_DOT_DELAY,
            },
          },
        }}
        className={styles.dotFill}
        initial={{ width: '0%' }}
      />
    </motion.div>
  );
});

export const WizardMobileProgress = forwardRef<HTMLDivElement, WizardMobileProgressProps>(
  ({ items, currentItemId, currentStepIndex, visibleDots = DEFAULT_VISIBLE_DOTS, onAction }, ref) => {
    const { itemId, subItemId } = useMemo(() => parseItemId(currentItemId), [currentItemId]);
    const currentItemIndex = useMemo(() => {
      return items.findIndex(item => item.id === itemId);
    }, [items, itemId]);
    const currentItem = items[currentItemIndex];
    const currenSubItem = currentItem.subItems?.find(subItem => subItem.id === subItemId);
    const { s } = useI18nContext();

    const [isOpen, setIsOpen] = useState(false);

    // this calculates the full amount needed for each dot item based on the current item index and the current step index
    const fill = useMemo(() => {
      const currentItemLastStepIndex = items.slice(0, currentItemIndex).reduce((acc, item) => acc + item.stepsCount, 0);
      const gap = Math.abs(currentItemLastStepIndex - currentStepIndex);

      if (Number.isNaN(gap)) return 0;

      return new Array(gap).fill(0).reduce<number>(acc => acc + (1 - acc) * MIN_DOT_FILL_AMOUNT, 0);
    }, [items, currentItemIndex, currentStepIndex]);

    // this calculates the left position of the progress bar "window" based on the current item index, visible dots and total items
    const left = useMemo(() => {
      const middleIndex = Math.floor(visibleDots / 2);

      if (currentItemIndex >= visibleDots - 1 && currentItemIndex <= items.length - middleIndex) {
        return styles.STEP_WIDTH_AND_PADDING * -(currentItemIndex - visibleDots + middleIndex);
      } else if (currentItemIndex > items.length - middleIndex) {
        return styles.STEP_WIDTH_AND_PADDING * -(items.length - visibleDots);
      } else {
        return 0;
      }
    }, [currentItemIndex, items.length, visibleDots]);

    const title = currenSubItem?.title ?? currentItem.title;

    return (
      <Drawer.Root direction="top" onOpenChange={setIsOpen} open={isOpen}>
        <Drawer.Trigger>
          <Flex
            alignItems="center"
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            flexDirection="column"
            gap={spacing.s08}
            id={WIZARD_PROGRESS_ELEMENT_ID}
            initial={{ opacity: 0 }}
            ref={ref}
          >
            <AnimatePresence mode="popLayout">
              <Flex
                animate={{ x: 0, opacity: 1 }}
                as={motion.div}
                exit={{ x: '-100%', opacity: 0 }}
                initial={{ x: '100%', opacity: 0 }}
                justifyContent="center"
                key={`${currentItem.id}-${currenSubItem?.id}`}
                minWidth="100%"
                textAlign="center"
                transition={springAnimationConfiguration}
              >
                <Text className={ellipsis} data-active="true" type="h6">
                  {title}
                </Text>
              </Flex>
            </AnimatePresence>
            <Flex
              overflow="hidden"
              width={`${(visibleDots - 1) * styles.STEP_WIDTH_AND_PADDING + getPixelValue(styles.DOT_OPENED_WIDTH)}px`}
            >
              <Flex
                animate={{ opacity: 1 }}
                as={motion.div}
                className={styles.window}
                gap={styles.DOTS_GAP}
                initial={{ opacity: 0 }}
                position="relative"
                style={{ left }}
                transition={springAnimationConfiguration}
              >
                <AnimatePresence initial={false} mode="popLayout">
                  {items.map((item, index) => (
                    <Dot
                      current={index === currentItemIndex}
                      fill={index > currentItemIndex ? 0 : fill}
                      key={item.id}
                      stepCompleted={index < currentItemIndex}
                    />
                  ))}
                </AnimatePresence>
              </Flex>
            </Flex>
          </Flex>
        </Drawer.Trigger>
        <Drawer.Content description={s('wizard_mobile_progress.title')} title={s('wizard_mobile_progress.title')}>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: PROGRESS_ANIMATION_DURATION }}
              >
                <WizardProgress currentItemId={currentItemId} items={items} onAction={onAction} />
              </motion.div>
            )}
          </AnimatePresence>
        </Drawer.Content>
      </Drawer.Root>
    );
  },
);
